export interface QueuedWord {
  documentId: string;
  index: number;
  word: string;
}

export interface MessageQueueEntry {
  documentId: string;
  words: QueuedWord[];
  enqueuedAt: number;
}

export type WordTypedCallback = (documentId: string, index: number, word: string) => void;
export type MessageCompleteCallback = (documentId: string) => void;

/**
 * Types words one-by-one with adaptive speed. Processes messages sequentially
 * in enqueue order. Speeds up when the buffer grows, slows down when it shrinks.
 */
export class TypingQueue {
  private queues: MessageQueueEntry[] = [];
  private currentDocId: string | null = null;
  private typingActive = false;
  private typingTimer: ReturnType<typeof setTimeout> | null = null;

  private onWordTyped: WordTypedCallback;
  private onMessageComplete: MessageCompleteCallback;

  private baseSpeedMs: number;
  private varianceMs: number;
  private minSpeedMs = 170;
  private maxSpeedMs = 280;
  private targetBufferWords = 5;
  private maxBufferWords = 20;

  // Fairness: max words typed for one message before yielding to another that is
  // waiting. Prevents a never-ending speaker from starving later messages.
  private fairnessBudget = 12;
  private consecutiveWords = 0;

  constructor(
    onWordTyped: WordTypedCallback,
    onMessageComplete: MessageCompleteCallback,
    baseSpeedMs: number = 170,
    varianceMs: number = 30
  ) {
    this.onWordTyped = onWordTyped;
    this.onMessageComplete = onMessageComplete;
    this.baseSpeedMs = baseSpeedMs;
    this.varianceMs = varianceMs;
    this.minSpeedMs = Math.round(baseSpeedMs * 0.8);
    this.maxSpeedMs = Math.round(baseSpeedMs * 1.6);
  }

  /** Adds words to a message's queue. Deduplicates by index and sorts by position. */
  enqueue(documentId: string, words: Array<{ index: number; word: string }>): void {
    if (words.length === 0) return;

    let entry = this.queues.find(q => q.documentId === documentId);
    if (!entry) {
      entry = { documentId, words: [], enqueuedAt: Date.now() };
      this.queues.push(entry);
    }

    for (const w of words) {
      if (!entry.words.some(qw => qw.index === w.index)) {
        entry.words.push({ documentId, index: w.index, word: w.word });
      }
    }
    entry.words.sort((a, b) => a.index - b.index);

    this.startTyping();
  }

  remove(documentId: string): void {
    this.queues = this.queues.filter(q => q.documentId !== documentId);
    if (this.currentDocId === documentId) this.currentDocId = null;
  }

  isQueued(documentId: string): boolean {
    return this.currentDocId === documentId ||
      this.queues.some(q => q.documentId === documentId && q.words.length > 0);
  }

  getPendingCount(documentId: string): number {
    return this.queues.find(q => q.documentId === documentId)?.words.length ?? 0;
  }

  get currentlyTyping(): string | null {
    return this.currentDocId;
  }
  get isActive(): boolean {
    return this.typingActive;
  }

  setSpeed(baseMs: number, varianceMs: number = 50): void {
    this.baseSpeedMs = baseMs;
    this.varianceMs = varianceMs;
    this.minSpeedMs = Math.round(baseMs * 0.8);
    this.maxSpeedMs = Math.round(baseMs * 2);
  }

  /** Stops all typing and clears the queue. Call before committing words instantly. */
  flush(): void {
    if (this.typingTimer) clearTimeout(this.typingTimer);
    this.typingTimer = null;
    this.queues = [];
    this.currentDocId = null;
    this.typingActive = false;
    this.consecutiveWords = 0;
  }

  destroy(): void {
    this.flush();
  }

  // --- Typing Logic ---

  private startTyping(): void {
    if (this.typingActive) return;
    if (!this.currentDocId) this.currentDocId = this.getNextMessage();
    if (!this.currentDocId) return;

    this.typingActive = true;
    this.typeNextWord();
  }

  private getNextMessage(): string | null {
    const withWords = this.queues
      .filter(q => q.words.length > 0)
      .sort((a, b) => a.enqueuedAt - b.enqueuedAt);
    return withWords[0]?.documentId ?? null;
  }

  private typeNextWord(): void {
    if (!this.currentDocId) {
      this.currentDocId = this.getNextMessage();
      this.consecutiveWords = 0;
    }

    if (!this.currentDocId) {
      this.typingActive = false;
      return;
    }

    let entry = this.queues.find(q => q.documentId === this.currentDocId);

    if (!entry || entry.words.length === 0) {
      const completedDocId = this.currentDocId;
      this.currentDocId = null;
      this.consecutiveWords = 0;

      this.onMessageComplete(completedDocId);

      // Find next AFTER callback (it may have re-enqueued words)
      this.currentDocId = this.getNextMessage();
      if (this.currentDocId) {
        this.scheduleNextWord();
      } else {
        this.typingActive = false;
      }
      return;
    }

    // Past the budget, yield to another waiting message so a continuous speaker
    // can't hold the head of the queue and starve the rest.
    if (this.consecutiveWords >= this.fairnessBudget) {
      const other = this.queues.find(
        q => q.documentId !== this.currentDocId && q.words.length > 0
      );
      if (other) {
        this.currentDocId = other.documentId;
        this.consecutiveWords = 0;
        entry = other;
      }
    }

    const queuedWord = entry.words.shift()!;
    this.consecutiveWords++;
    this.onWordTyped(queuedWord.documentId, queuedWord.index, queuedWord.word);
    this.scheduleNextWord();
  }

  private scheduleNextWord(): void {
    this.typingTimer = setTimeout(() => this.typeNextWord(), this.getTypingDelay());
  }

  /** Calculate adaptive speed based on queue size (no variance or rounding). */
  private calculateAdaptiveSpeed(): number {
    const pending = this.getTotalPendingWords();

    if (pending <= this.targetBufferWords) return this.maxSpeedMs;
    if (pending >= this.maxBufferWords) return this.minSpeedMs;

    const ratio = (pending - this.targetBufferWords) / (this.maxBufferWords - this.targetBufferWords);
    return this.maxSpeedMs - ratio * (this.maxSpeedMs - this.minSpeedMs);
  }

  /** Adaptive delay: slow (maxSpeedMs) when buffer is small, fast (minSpeedMs) when large. */
  private getTypingDelay(): number {
    const targetSpeed = this.calculateAdaptiveSpeed();
    const variance = (Math.random() - 0.5) * 2 * this.varianceMs;
    return Math.max(this.minSpeedMs, targetSpeed + variance);
  }

  private getTotalPendingWords(): number {
    return this.queues.reduce((sum, q) => sum + q.words.length, 0);
  }

  // --- Debug ---

  toDebugObject(): object {
    const pendingWords = this.getTotalPendingWords();
    return {
      currentlyTyping: this.currentDocId,
      isActive: this.typingActive,
      baseSpeedMs: this.baseSpeedMs,
      currentSpeedMs: this.getCurrentAdaptiveSpeed(),
      pendingWords,
      bufferHealth: pendingWords <= this.targetBufferWords ? 'healthy' :
                    pendingWords >= this.maxBufferWords ? 'critical' : 'catching-up',
      queueCount: this.queues.length,
      queues: this.queues.map(q => ({
        documentId: q.documentId,
        wordCount: q.words.length,
        enqueuedAt: q.enqueuedAt,
        words: q.words.slice(0, 5).map(w => w.word)
      }))
    };
  }

  private getCurrentAdaptiveSpeed(): number {
    return Math.round(this.calculateAdaptiveSpeed());
  }
}
